import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import AuthLoading from "components/common/AuthLoading";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import "./layout.css";

const WORKSPACE_QUERY_PARAM = "wid";
const LEGACY_WORKSPACE_QUERY_PARAM = "workspaceId";
const STARTER_WORKSPACE_DRAFT_KEY = "ovacusInitialWorkspaceDraft";
const LINKED_ACCOUNT_LOGIN_KEY = "ovacusLinkedAccountLogin";

const hasStarterWorkspaceDraft = () => {
  if (typeof window === "undefined") return false;
  try {
    return Boolean(window.localStorage.getItem(STARTER_WORKSPACE_DRAFT_KEY));
  } catch {
    return false;
  }
};

const hasLinkedAccountLogin = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(LINKED_ACCOUNT_LOGIN_KEY) === "1";
  } catch {
    return false;
  }
};

const clearLinkedAccountLogin = () => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LINKED_ACCOUNT_LOGIN_KEY);
  } catch {
    // Session storage is best effort only.
  }
};

const isWorkspaceInvitationRoute = (pathname = "") =>
  /^\/workspaces\/invitations\/accept\/?$/.test(pathname) ||
  /^\/workspaces\/[^/]+\/invitations\/[^/]+\/?$/.test(pathname);

function WorkspaceUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    workspaces,
    selectedWorkspaceId,
    selectWorkspace,
    loading,
  } = useWorkspace();

  const searchParams = new URLSearchParams(location.search);
  const urlWorkspaceId =
    searchParams.get(WORKSPACE_QUERY_PARAM) ||
    searchParams.get(LEGACY_WORKSPACE_QUERY_PARAM) ||
    "";
  const urlWorkspaceMatchesAccount = Boolean(
    urlWorkspaceId &&
      workspaces.some((account) => account?.id === urlWorkspaceId),
  );

  useEffect(() => {
    if (
      !urlWorkspaceId ||
      !urlWorkspaceMatchesAccount ||
      urlWorkspaceId === selectedWorkspaceId
    ) {
      return;
    }

    selectWorkspace(urlWorkspaceId);
  }, [
    selectedWorkspaceId,
    selectWorkspace,
    urlWorkspaceId,
    urlWorkspaceMatchesAccount,
  ]);

  useEffect(() => {
    if (loading) return;

    const nextParams = new URLSearchParams(location.search);
    const currentWorkspaceId =
      nextParams.get(WORKSPACE_QUERY_PARAM) ||
      nextParams.get(LEGACY_WORKSPACE_QUERY_PARAM) ||
      "";

    if (
      urlWorkspaceId &&
      urlWorkspaceMatchesAccount &&
      urlWorkspaceId !== selectedWorkspaceId
    ) {
      return;
    }

    if (selectedWorkspaceId) {
      if (
        currentWorkspaceId === selectedWorkspaceId &&
        nextParams.get(WORKSPACE_QUERY_PARAM) === selectedWorkspaceId &&
        !nextParams.get(LEGACY_WORKSPACE_QUERY_PARAM)
      ) {
        return;
      }
      nextParams.set(WORKSPACE_QUERY_PARAM, selectedWorkspaceId);
    } else {
      if (!currentWorkspaceId) return;
      nextParams.delete(WORKSPACE_QUERY_PARAM);
    }
    nextParams.delete(LEGACY_WORKSPACE_QUERY_PARAM);

    const nextSearch = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true },
    );
  }, [
    loading,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    selectedWorkspaceId,
    urlWorkspaceId,
    urlWorkspaceMatchesAccount,
  ]);

  return null;
}

function WorkspaceSetupRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaces, selectedWorkspaceId, loading, workspaceListReady, error } = useWorkspace();
  const allowWithoutWorkspace = isWorkspaceInvitationRoute(location.pathname);

  useEffect(() => {
    if (workspaceListReady && workspaces.length > 0) {
      clearLinkedAccountLogin();
    }

    if (
      loading ||
      !workspaceListReady ||
      error ||
      selectedWorkspaceId ||
      workspaces.length > 0 ||
      allowWithoutWorkspace ||
      hasLinkedAccountLogin()
    ) {
      return;
    }

    const setupReason = hasStarterWorkspaceDraft() ? "first-time" : "repair";

    navigate(paths.getStarted, {
      replace: true,
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        source: "missing-workspace",
        setupReason,
      },
    });
  }, [
    loading,
    location.hash,
    location.pathname,
    location.search,
    navigate,
    error,
    selectedWorkspaceId,
    workspaces.length,
    workspaceListReady,
    allowWithoutWorkspace,
  ]);

  return null;
}

export default function PMLayout() {
  const location = useLocation();
  const isDocsRoute = location.pathname === "/docs" || location.pathname.startsWith("/docs/");
  const {
    workspaces,
    selectedWorkspace,
    selectedWorkspaceId,
    loading,
    workspaceListReady,
    error,
  } = useWorkspace();
  const allowWithoutWorkspace = isWorkspaceInvitationRoute(location.pathname);

  const waitingForWorkspaceVerification =
    loading ||
    !workspaceListReady ||
    (!error && selectedWorkspaceId && !selectedWorkspace) ||
    (!error &&
      workspaceListReady &&
      workspaces.length === 0 &&
      !allowWithoutWorkspace &&
      !hasLinkedAccountLogin());

  if (waitingForWorkspaceVerification) {
    return (
      <>
        <WorkspaceSetupRedirect />
        <AuthLoading message="Processing..." />
      </>
    );
  }

  return (
    <div className="pm-root">
      <WorkspaceUrlSync />
      <WorkspaceSetupRedirect />
      <Sidebar />
      <div className="pm-main">
        <Topbar />
        <main className={`pm-cnt${isDocsRoute ? " pm-cnt-docs" : ""}`}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
